import { mountReactApp, showErrorUI, formatMountError } from "./mainHelpers";
import "./index.css";
import { initSentry } from "./services/sentry";
import * as Sentry from "@sentry/react";
import { ensureBlockOnlyServiceWorker } from "./utils/blockOnlyServiceWorker";
import {
  clearChunkReloadFlag,
  clearChunkReloadPendingFlag,
  setupChunkLoadRecovery,
} from "./utils/lazyWithRetry";
import { setupBuildVersionPoll } from "./utils/buildVersionPoll";
import { waitForRuntimeConfig } from "./utils/waitForRuntimeConfig";
import { getSentryDsn } from "./utils/getSentryDsn";
import { initWebMcp } from "./utils/webMcp";
import { installGlobalErrorHandlers } from "./utils/globalErrorHandlers";

setupChunkLoadRecovery();
initWebMcp();
setupBuildVersionPoll();
// New page boot succeeded after a chunk-recovery reload; stop suppressing non-chunk Sentry events.
clearChunkReloadPendingFlag();
installGlobalErrorHandlers();

// Add test function to window for easy testing
interface WindowWithTestSentry extends Window {
  testSentry?: () => void;
  __SENTRY_CONFIGURED__?: boolean;
}
(globalThis.window as WindowWithTestSentry).testSentry = () => {
  if (!getSentryDsn()) {
    console.warn("❌ Sentry DSN not configured — set VITE_SENTRY_DSN in config.js or build env.");
    return;
  }

  console.warn("🧪 Sending test event to Sentry...");
  try {
    const testError = new Error("Test Sentry Error from Frontend");
    testError.name = "TestSentryError";
    Sentry.captureException(testError, {
      level: "error",
      tags: {
        test: true,
        forceSend: true,
        source: "frontend-test-function",
      },
      extra: {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      },
    });

    Sentry.captureMessage("Test message from TellerWand Frontend", {
      level: "info",
      tags: {
        test: true,
        forceSend: true,
        source: "frontend-test-function",
      },
    });

    console.warn("✅ Test error and message sent to Sentry. Check your dashboard.");
  } catch (error) {
    console.error("❌ Failed to send test error to Sentry:", error);
  }
};
if (import.meta.env.DEV) {
  console.warn("💡 Test Sentry: Run testSentry() in the console to send a test error");
}

// Note: Challenge-platform interceptor is set up in index.html as an inline script
// to ensure it runs before any other scripts (including Turnstile) can make requests

// Initialize Supabase interceptor safely
import("./integrations/supabase/interceptor.ts")
  .then(({ initSupabaseInterceptor }) => {
    initSupabaseInterceptor();
  })
  .catch((error) => {
    console.warn("Failed to initialize Supabase interceptor:", error);
  });

// Register Turnstile-safe service worker and remove legacy PWA workers that blocked Cloudflare challenges.
if ("serviceWorker" in navigator) {
  globalThis.window.addEventListener("load", () => {
    ensureBlockOnlyServiceWorker().catch((error) => {
      if (process.env.NODE_ENV === "development") {
        console.error("❌ Failed to register block-only service worker:", error);
      }
    });
  });
} else {
  console.warn("⚠️ Service Workers are not supported in this browser");
}

// Initialize React app with comprehensive error handling
// First, ensure root element exists and show immediate feedback
const rootElement = document.getElementById("root");

// Don't clear innerHTML - let React replace it naturally
// This prevents empty root if React fails to mount

// Function to show error UI — delegates to mainHelpers
function showBootstrapErrorUI(message: string, error?: Error) {
  showErrorUI(rootElement, message, error);
}

if (rootElement) {
  // Wait for runtime config, init Sentry, then mount React
  waitForRuntimeConfig()
    .then(() => {
      const sentryReady = initSentry();
      (globalThis.window as WindowWithTestSentry).__SENTRY_CONFIGURED__ = sentryReady;
      try {
        mountReactApp(rootElement);
        clearChunkReloadFlag();
      } catch (error) {
        console.error("❌ Failed to mount React app:", error);
        console.error("Error details:", formatMountError(error));
        showBootstrapErrorUI("Failed to initialize application", error as Error);
      }
    })
    .catch((error) => {
      console.error("❌ Failed to wait for config:", error);
      try {
        mountReactApp(rootElement);
      } catch (renderError) {
        showBootstrapErrorUI("Failed to initialize application", renderError as Error);
      }
    });
} else {
  console.error("❌ Root element not found!");
  showBootstrapErrorUI("Root element not found");
}
